import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import type { ContactFieldSelection, Employee } from "../lib/types";

const DEFAULT_SELECTION: ContactFieldSelection = {
  mobile: true,
  secondaryMobile: false,
  officeNumber: true,
  whatsapp: false,
  email: true,
  secondaryEmail: false,
  company: true,
  title: true,
  department: false,
  address: false,
  website: false,
};

type FormState = Partial<Employee> & { contactFieldSelection: ContactFieldSelection };

const EMPTY: FormState = {
  employeeId: "",
  firstName: "",
  lastName: "",
  displayName: "",
  jobTitle: "",
  companyName: "",
  contactFieldSelection: DEFAULT_SELECTION,
};

export default function EmployeeForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    if (!id) return;
    api<{ employee: Employee }>(`/employees/${id}`).then((res) =>
      setForm({ ...res.employee, contactFieldSelection: res.employee.contactFieldSelection ?? DEFAULT_SELECTION })
    );
  }, [id]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setSelection(key: keyof ContactFieldSelection, value: boolean) {
    setForm((f) => ({ ...f, contactFieldSelection: { ...f.contactFieldSelection, [key]: value } }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      let employeeId = id;
      if (isEdit) {
        await api(`/employees/${id}`, { method: "PUT", body: form });
      } else {
        const res = await api<{ employee: Employee }>("/employees", { method: "POST", body: form });
        employeeId = res.employee.id;
      }
      if (photoFile && employeeId) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        await api(`/employees/${employeeId}/photo`, { method: "POST", body: fd, isForm: true });
      }
      navigate(`/employees/${employeeId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message ?? err.code : "Failed to save employee.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="page-title">{isEdit ? "Edit Business Card" : "Create New Business Card"}</h1>
          <div className="page-subtitle">Fields marked with the admin's judgment become part of the employee's public contact QR/VCF unless excluded below.</div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="form-section-title">Personal Information</div>
          <div className="form-grid">
            <div className="field">
              <label>Employee ID *</label>
              <input required value={form.employeeId ?? ""} onChange={(e) => set("employeeId", e.target.value)} disabled={isEdit} />
            </div>
            <div className="field">
              <label>First Name *</label>
              <input required value={form.firstName ?? ""} onChange={(e) => set("firstName", e.target.value)} />
            </div>
            <div className="field">
              <label>Middle Name</label>
              <input value={form.middleName ?? ""} onChange={(e) => set("middleName", e.target.value)} />
            </div>
            <div className="field">
              <label>Last Name *</label>
              <input required value={form.lastName ?? ""} onChange={(e) => set("lastName", e.target.value)} />
            </div>
            <div className="field">
              <label>Display Name *</label>
              <input required value={form.displayName ?? ""} onChange={(e) => set("displayName", e.target.value)} />
            </div>
            <div className="field">
              <label>Arabic Name</label>
              <input dir="rtl" value={form.arabicName ?? ""} onChange={(e) => set("arabicName", e.target.value)} />
            </div>
            <div className="field">
              <label>Profile Photograph</label>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)} />
              {isEdit && form.photo?.thumbnailUrl && (
                <img src={form.photo.thumbnailUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, marginTop: 6, objectFit: "cover" }} />
              )}
            </div>
          </div>

          <div className="form-section-title">Job Information</div>
          <div className="form-grid">
            <div className="field">
              <label>Job Title *</label>
              <input required value={form.jobTitle ?? ""} onChange={(e) => set("jobTitle", e.target.value)} />
            </div>
            <div className="field">
              <label>Arabic Job Title</label>
              <input dir="rtl" value={form.arabicJobTitle ?? ""} onChange={(e) => set("arabicJobTitle", e.target.value)} />
            </div>
            <div className="field">
              <label>Department</label>
              <input value={form.department ?? ""} onChange={(e) => set("department", e.target.value)} />
            </div>
            <div className="field">
              <label>Branch</label>
              <input value={form.branch ?? ""} onChange={(e) => set("branch", e.target.value)} />
            </div>
            <div className="field">
              <label>Office</label>
              <input value={form.office ?? ""} onChange={(e) => set("office", e.target.value)} />
            </div>
            <div className="field">
              <label>Floor</label>
              <input value={form.floor ?? ""} onChange={(e) => set("floor", e.target.value)} />
            </div>
            <div className="field">
              <label>Employee Number</label>
              <input value={form.employeeNumber ?? ""} onChange={(e) => set("employeeNumber", e.target.value)} />
            </div>
          </div>

          <div className="form-section-title">Contact Information</div>
          <div className="form-grid">
            <div className="field">
              <label>Primary Mobile</label>
              <input value={form.mobile ?? ""} onChange={(e) => set("mobile", e.target.value)} placeholder="+9715xxxxxxxx" />
            </div>
            <div className="field">
              <label>Secondary Mobile</label>
              <input value={form.secondaryMobile ?? ""} onChange={(e) => set("secondaryMobile", e.target.value)} />
            </div>
            <div className="field">
              <label>Office Telephone</label>
              <input value={form.officeTelephone ?? ""} onChange={(e) => set("officeTelephone", e.target.value)} />
            </div>
            <div className="field">
              <label>Extension</label>
              <input value={form.extension ?? ""} onChange={(e) => set("extension", e.target.value)} />
            </div>
            <div className="field">
              <label>Email</label>
              <input type="email" value={form.email ?? ""} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="field">
              <label>Secondary Email</label>
              <input type="email" value={form.secondaryEmail ?? ""} onChange={(e) => set("secondaryEmail", e.target.value)} />
            </div>
            <div className="field">
              <label>WhatsApp Number</label>
              <input value={form.whatsapp ?? ""} onChange={(e) => set("whatsapp", e.target.value)} />
            </div>
          </div>

          <div className="form-section-title">Company Information</div>
          <div className="form-grid">
            <div className="field">
              <label>Company / Mall Name *</label>
              <input required value={form.companyName ?? ""} onChange={(e) => set("companyName", e.target.value)} />
            </div>
            <div className="field">
              <label>Company Address</label>
              <input value={form.companyAddress ?? ""} onChange={(e) => set("companyAddress", e.target.value)} />
            </div>
            <div className="field">
              <label>PO Box</label>
              <input value={form.poBox ?? ""} onChange={(e) => set("poBox", e.target.value)} />
            </div>
            <div className="field">
              <label>City</label>
              <input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div className="field">
              <label>Country</label>
              <input value={form.country ?? ""} onChange={(e) => set("country", e.target.value)} />
            </div>
            <div className="field">
              <label>Website</label>
              <input value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} />
            </div>
            <div className="field">
              <label>LinkedIn</label>
              <input value={form.linkedin ?? ""} onChange={(e) => set("linkedin", e.target.value)} />
            </div>
            <div className="field">
              <label>Instagram</label>
              <input value={form.instagram ?? ""} onChange={(e) => set("instagram", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="form-section-title" style={{ marginTop: 0 }}>
            QR / Wallet Privacy — fields to embed in the contact QR, VCF and Wallet passes
          </div>
          <div className="alert alert-warning">Any person who can scan this QR can read the contact details embedded in it. Only enable fields the employee has agreed to share publicly.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {(Object.keys(form.contactFieldSelection) as Array<keyof ContactFieldSelection>).map((key) => (
              <label className="checkbox-row" key={key}>
                <input type="checkbox" checked={form.contactFieldSelection[key]} onChange={(e) => setSelection(key, e.target.checked)} />
                {key}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
          <button className="btn btn-gold" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Business Card"}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
